import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon/Icon";
import { useSheetSurface } from "@ui/components/SheetSurface";
import { TopBarBorder } from "@ui/components/TopBar/TopBarBorder/TopBarBorder";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import type { ReactNode } from "react";
import { SurfaceCloseButton } from "./SurfaceCloseButton";
import { SurfaceTitle } from "./SurfaceTitle";
import { sheetHeader, surfaceCloseButton, surfaceHeaderRow } from "./sheet-chrome.styles";

export interface SurfaceHeaderProps {
  /** Title rendered in both the mobile bar and the desktop header. */
  title: string;
  /** Secondary text shown below the title (e.g. a count or status). */
  subtitle?: string;
  /** Action elements rendered to the right of the title on desktop and in the mobile actions slot. */
  actions?: ReactNode;
  className?: string;
  /** Controls the desktop close (X) button visibility. Defaults to the Dialog's `dismissible`. */
  dismissible?: boolean;
  /**
   * Mobile nav control glyph: "close" (X, trailing on sheets) or "back"
   * (arrow, leading — for stepping back inside a flow). Defaults to "close".
   */
  navIcon?: "back" | "close";
  /** Called when the mobile nav control is pressed. Defaults to closing the Dialog. */
  onClose?: () => void;
  /**
   * Mobile treatment. "sheet" is the card sheet's compact title bar; "page" is
   * the fullscreen `TopBarMobile`. Bound by the surface that re-exports this
   * as its `Header`, never chosen at a call site.
   */
  layout?: "sheet" | "page";
}

/**
 * The shared surface header, in the treatment its `layout` selects.
 *
 * `layout="sheet"` on mobile: a title bar at the sheet's top edge with a
 * centered title and a nav control — a trailing close (X) by default, or a
 * leading back arrow via `navIcon="back"` — the focusable dismiss path that
 * swipe and scrim tap (pointer-only) cannot provide. When the surface's
 * `grabber` is on, the grabber replaces the nav control (the two are
 * mutually exclusive). `layout="page"` on mobile: a `TopBarMobile`-shaped bar
 * with a leading nav control. Desktop (either layout): when `actions` is
 * provided, a centered three-column layout (close-left, title+subtitle-center,
 * actions-right); otherwise title-left / close-right.
 *
 * A single sr-only `Sheet.Title` provides the surface's accessible name at
 * every breakpoint (Silk wires `aria-labelledby` automatically); visible
 * title text is rendered separately so it can be styled per breakpoint
 * without duplicating the heading element.
 */
export function SurfaceHeader({
  title,
  subtitle,
  actions,
  className,
  dismissible,
  navIcon = "close",
  onClose,
  layout = "sheet",
}: SurfaceHeaderProps) {
  const ctx = useSheetSurface();
  const close = onClose ?? ctx.close;
  const canDismiss = dismissible ?? ctx.dismissal.dismissible;

  const srTitle = <SurfaceTitle className="sr-only">{title}</SurfaceTitle>;

  if (ctx.platform !== "desktop" && layout === "sheet") {
    const styles = sheetHeader({ grabber: ctx.grabber });
    // The grabber is the dismissal affordance when present; otherwise the bar
    // carries the nav control (back is navigation, so it isn't gated on
    // dismissability the way the close X is).
    const showBack = !ctx.grabber && navIcon === "back";
    const showClose = !ctx.grabber && navIcon === "close" && canDismiss;
    return (
      <div className={styles.root({ className })}>
        {srTitle}
        <div className={styles.row()}>
          {showBack && (
            <div className={styles.lead()}>
              <button
                aria-label={t("common.actions.back")}
                className={surfaceCloseButton({ className: "me-0 mt-0" })}
                onClick={close}
                type="button"
              >
                <Icon name="arrowLeft" size="20" />
              </button>
            </div>
          )}
          <span aria-hidden className={styles.title()}>
            {title}
          </span>
          {(actions || showClose) && (
            <div className={styles.trail()}>
              {actions}
              {showClose && <SurfaceCloseButton className="me-0 mt-0" />}
            </div>
          )}
        </div>
        {subtitle && <span className={styles.subtitle()}>{subtitle}</span>}
      </div>
    );
  }

  const mobileBottomContent = subtitle ? (
    <>
      <div className="flex justify-center bg-white px-5 pb-2 text-neutral-500 text-sm">{subtitle}</div>
      <TopBarBorder />
    </>
  ) : undefined;

  return (
    <>
      {srTitle}
      <TopBarMobile
        actions={actions}
        bordered={!subtitle}
        bottomContent={mobileBottomContent}
        centered
        className="md:hidden"
        navIcon={navIcon}
        onBack={close}
        title={title}
      />
      {actions ? (
        <div className={surfaceHeaderRow({ className })}>
          {canDismiss ? <SurfaceCloseButton className="-ms-2" /> : <div className="size-11 shrink-0" />}
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-bold text-base text-neutral-900">{title}</span>
            {subtitle && <span className="text-neutral-500 text-sm">{subtitle}</span>}
          </div>
          <div className="flex shrink-0 items-center">{actions}</div>
        </div>
      ) : (
        <div className={surfaceHeaderRow({ className })}>
          {subtitle ? (
            <div className="flex flex-col gap-0.5">
              <span className="font-extrabold text-2xl text-neutral-900 tracking-2xl">{title}</span>
              <span className="text-neutral-500 text-sm">{subtitle}</span>
            </div>
          ) : (
            <span className="font-extrabold text-2xl text-neutral-900 tracking-2xl">{title}</span>
          )}
          {canDismiss && <SurfaceCloseButton />}
        </div>
      )}
    </>
  );
}
